import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ShieldCheck, CreditCard, Bell, Lock, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";

const scamTabs = [
  {
    key: "Cryptocurrency", title: "Cryptocurrency Scams",
    intro: "Fake exchanges, investment platforms, and Bitcoin schemes targeting both novice and experienced investors",
    flags: ["Promises of guaranteed high returns (legitimate crypto is volatile—no one can guarantee profits)", "Pressure to invest quickly before a 'deadline' or 'exclusive opportunity' expires", "Requests to send crypto to 'verify your account' or 'unlock withdrawals'", "Platform not registered with FinCEN, SEC, or your country's financial authority", "Withdrawal fees that keep increasing—first 'taxes,' then 'insurance,' then 'security deposits'"],
    examples: ["Fake trading platforms showing artificial profits that disappear when you try to withdraw", "Romance scammers gradually steering conversations toward crypto 'investments'", "Phishing sites mimicking Coinbase, Binance, or other legitimate exchanges", "Ponzi schemes promising 1-3% daily returns (mathematically impossible to sustain)"],
    recovery: "Crypto recovery is harder than credit cards but not impossible. If funds moved through a regulated exchange (Coinbase, Binance, Kraken), law enforcement can sometimes freeze accounts. Document every wallet address and transaction hash immediately.",
    now: "Check if the platform is registered at FinCEN.gov or your country's financial regulator before depositing any funds.",
  },
  {
    key: "Romance", title: "Romance Scams",
    intro: "Emotional manipulation through fake online relationships, often escalating to investment or emergency requests",
    flags: ["Professes strong feelings unusually quickly", "Always has an excuse to avoid video calls or meeting in person", "Emergencies that require money—medical bills, customs fees, travel costs", "Gradually introduces 'investment opportunities' you can share together", "Asks you to keep the relationship secret from family and friends"],
    examples: ["An 'overseas engineer' or 'military officer' who can never meet", "Requests starting small and escalating over months", "Sudden crypto 'investment' opportunities framed as a shared future", "Refusal to video chat citing poor connection or restricted access"],
    recovery: "Preserve all messages across every platform. File a comprehensive police report and connect with support groups. Documentation is critical for law enforcement referrals and any financial dispute.",
    now: "Reverse image search any profile photos and stop all further payments immediately.",
  },
  {
    key: "Investment", title: "Investment & Forex Fraud",
    intro: "Ponzi schemes, fake brokers, and unregulated forex platforms with fabricated trades and profits",
    flags: ["Unregistered 'brokers' offering leverage and mentorship", "Consistent, unrealistic returns with no losing periods", "Escalating 'tax' or 'fee' demands before withdrawals", "Pressure to recruit others or reinvest profits", "Platform screenshots that can't be independently verified"],
    examples: ["Simulated trading dashboards showing fictional profits", "Withdrawal requests blocked by new fees each time", "Brokers unregistered with financial authorities", "Guaranteed-return schemes that collapse when new deposits slow"],
    recovery: "Credit card deposits offer the strongest chargeback path. Gather platform screenshots and verify broker registration status—unregulated platforms operate outside investor protections.",
    now: "Verify broker registration with the SEC, FINRA, or your national regulator before depositing.",
  },
  {
    key: "Recovery", title: "Recovery Scams",
    intro: "Fake recovery services that target people who have already been defrauded, promising to get money back for a fee",
    flags: ["Contacts you unprompted claiming they can recover your lost funds", "Requires an upfront fee, 'tax,' or 'deposit' before any work", "Claims a guaranteed success rate", "Impersonates government agencies or law firms", "Pressures you to act quickly before a 'window closes'"],
    examples: ["'Fund recovery agents' demanding advance payment", "Impersonators posing as regulators offering to release frozen funds", "Fake law firms requesting retainers to pursue scammers", "Follow-up scams targeting known victim lists"],
    recovery: "Legitimate services do not charge upfront fees for consultations or guarantee results. If you've been targeted by a recovery scam, document it and report to authorities.",
    now: "Never pay an upfront fee to recover funds. Verify any organization independently before engaging.",
  },
];

const prevention = [
  { icon: ShieldCheck, title: "Verify Before You Trust", points: ["Research companies and individuals before sending money", "Check registration with regulatory bodies (SEC, FINRA, state agencies)", "Reverse image search profile photos", "Ask to video chat with anyone you've only met online"] },
  { icon: CreditCard, title: "Protect Payment Methods", points: ["Never send cryptocurrency to strangers", "Be suspicious of gift card payment requests", "Use credit cards for better fraud protection", "Set up transaction alerts on all accounts"] },
  { icon: Bell, title: "Recognize Pressure Tactics", points: ["Legitimate opportunities don't require immediate decisions", "Be wary of secrecy requests ('don't tell your family')", "Take time to consult with trusted friends or family", "If it feels wrong, trust your instincts"] },
  { icon: Lock, title: "Secure Your Digital Life", points: ["Use unique passwords for every account", "Enable two-factor authentication everywhere", "Never give remote access to strangers", "Keep software and systems updated"] },
];

export default function Insights() {
  const [active, setActive] = useState(scamTabs[0].key);
  const current = scamTabs.find((t) => t.key === active);

  return (
    <div data-testid="insights-page">
      <section className="hero-mesh section">
        <div className="container max-w-4xl text-center">
          <Reveal>
            <div className="trust-badge mx-auto mb-5">Expert Fraud Education</div>
            <h1 className="mb-5 font-display text-4xl font-extrabold tracking-tight text-primary md:text-5xl">
              Investment Scam Types & Prevention Guide
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Protect yourself with expert knowledge. Learn to identify cryptocurrency fraud, romance
              scams, Ponzi schemes, and recovery scams before they strike.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="mb-10 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold">Common Investment Scam Types</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Understanding how each scam type works is your first line of defense. Learn the red flags
              and protect yourself.
            </p>
          </Reveal>

          <div className="mb-8 flex flex-wrap justify-center gap-3">
            {scamTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`scam-tab-${t.key.toLowerCase()}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                  active === t.key
                    ? "bg-primary text-primary-foreground shadow-trust"
                    : "border border-border bg-white text-foreground/70 hover:border-secondary hover:text-secondary"
                }`}
              >
                {t.key}
              </button>
            ))}
          </div>

          <Reveal key={active} className="mx-auto max-w-4xl">
            <div className="calm-card">
              <h3 className="mb-2 font-display text-2xl font-semibold">{current.title}</h3>
              <p className="mb-6 text-muted-foreground">{current.intro}</p>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <h4 className="mb-3 flex items-center gap-2 font-semibold text-destructive"><AlertTriangle className="h-4 w-4" /> Red Flags</h4>
                  <ul className="space-y-2">
                    {current.flags.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" /> {f}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="mb-3 flex items-center gap-2 font-semibold text-trust">Common Examples</h4>
                  <ul className="space-y-2">
                    {current.examples.map((e) => (
                      <li key={e} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-trust" /> {e}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="mt-6 rounded-lg border border-secondary/10 bg-calm-light/40 p-4">
                <h4 className="mb-1 font-semibold text-secondary">Recovery Notes</h4>
                <p className="text-sm text-muted-foreground">{current.recovery}</p>
              </div>
              <div className="mt-4 rounded-lg border border-safe/20 bg-safe-light/50 p-4">
                <h4 className="mb-1 flex items-center gap-2 font-semibold text-safe"><CheckCircle2 className="h-4 w-4" /> What To Do Right Now</h4>
                <p className="text-sm text-muted-foreground">{current.now}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section bg-trust-light/60">
        <div className="container">
          <Reveal className="mb-10 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold">Investment Scam Prevention Guide</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              The best recovery is prevention. Use these expert-backed strategies to protect yourself and
              loved ones from financial fraud.
            </p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            {prevention.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="calm-card h-full hover-lift">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary/10"><p.icon className="h-5 w-5 text-secondary" /></div>
                    <h3 className="font-semibold">{p.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-safe" /> {pt}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container max-w-3xl text-center">
          <Reveal>
            <h2 className="mb-4 font-display text-3xl font-bold">Already a Victim? Get Confidential Guidance</h2>
            <p className="mx-auto mb-8 max-w-xl text-muted-foreground">
              Reach out to our team for personalized recovery guidance. No obligation, no pressure—just
              honest answers.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="insights-cta-contact">
              Contact Us <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
